import { v4 as uuid4 } from 'uuid'
import { computed, reactive } from 'vue'

import {
  useNotification as usePaprikaNotifications,
  type LNotificationParams
} from '@last/core-ui/paprika'
import type { LNotificationProps } from '@last/core-ui/paprika/components/Notifications/types'

type POSNotification = LNotificationParams & {
  type?: 'info' | 'success' | 'error'
  important?: boolean
}

const notifications: (LNotificationProps & { important?: boolean })[] = reactive([])

export const useNotifications = () => {
  const notification = usePaprikaNotifications()

  const amount = computed(() => notifications.length)

  function notifySuccess(constructor: POSNotification) {
    const id = constructor.id ?? uuid4()
    if (notifications.find(n => n.id === id)) {
      return id
    }
    const newNotification = {
      ...constructor,
      onAction: constructor.onAction
        ? () => {
            constructor.onAction?.()
            close(id)
          }
        : undefined,
      type: 'success' as const
    }
    notification.create(newNotification)
    notifications.unshift({ id, ...newNotification })
    return id
  }

  function notifyError(constructor: POSNotification) {
    const id = constructor.id ?? uuid4()
    if (notifications.find(n => n.id === id)) {
      return id
    }
    const newNotification = {
      ...constructor,
      onAction: constructor.onAction
        ? () => {
            constructor.onAction?.()
            close(id)
          }
        : undefined,
      type: 'error' as const
    }
    notification.create(newNotification)
    notifications.unshift({ id, ...newNotification })
    return id
  }

  function notifyInfo(constructor: POSNotification) {
    const newNotification = {
      ...constructor,
      type: 'info' as const
    }
    const id = notification.create(newNotification)
    return id
  }

  function close(id: string) {
    const index = notifications.findIndex(n => n.id === id)
    if (index !== -1) {
      notifications.splice(index, 1)
      notification.close(id)
    }
  }

  return {
    notifications,
    amount,
    close,
    notifySuccess,
    notifyError,
    notifyInfo
  }
}
